import { ZodSchema } from 'zod';
import { nanoid } from 'nanoid';
import {
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import {
  AuthResponse,
  AuthResponseSchema,
} from 'renderer/features/auth/schemas/auth.schemas';
import { logout, setCredentials } from 'renderer/features/auth/state/userSlice';
import {
  removeTokens,
  setTokens,
} from 'renderer/features/auth/state/authSlice';
// eslint-disable-next-line import/no-cycle
import { RootState } from './store';

const GUEST_ID_KEY = 'guestId';
const getOrCreateGuestId = (): string => {
  let guestId = localStorage.getItem(GUEST_ID_KEY);
  if (!guestId) {
    guestId = nanoid(12);
    localStorage.setItem(GUEST_ID_KEY, guestId);
  }
  return guestId;
};
// type TokenType = 'access';

interface ExtraOptions {
  responseSchema?: ZodSchema;
}
interface ExtraOptionsPrivate extends ExtraOptions {
  token?: string;
}

type BaseQueryPrivate = BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  ExtraOptionsPrivate,
  FetchBaseQueryMeta
>;

type BaseQuery = BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  ExtraOptions,
  FetchBaseQueryMeta
>;

// const getTokenFromStorage = (tokenType: TokenType): string | null => {
//   return localStorage.getItem(`${tokenType}Token`);
// };

const baseQueryWithAuth = (token?: string) =>
  fetchBaseQuery({
    baseUrl: `${process.env.SERVER_URL ?? 'http://localhost:3000'}/api`,
    credentials: 'include',
    prepareHeaders: async (headers) => {
      const guestId = getOrCreateGuestId();
      if (guestId) headers.set('x-guest-id', guestId);

      if (!token) return headers;

      headers.set('Authorization', `Bearer ${token}`);

      // headers.set('Accept', 'application/json');
      // headers.set('Cache-Control', 'no-cache');
      // headers.set('Pragma', 'no-cache');
      // headers.set('Expires', '0');

      return headers;
    },
  });

const baseQueryWithAuthAndZod: BaseQueryPrivate = async (
  args,
  api,
  extraOptions,
) => {
  // Run the provided query
  const { token } = extraOptions;
  const returnValue = await baseQueryWithAuth(token)(args, api, extraOptions);

  // Retrieve the data schema from the extraOptions object
  const responseSchema = extraOptions?.responseSchema;

  const { data } = returnValue;

  // Check if both 'data' and 'responseSchema' are defined
  if (data && responseSchema) {
    // throws Validation error if the 'data' fails validation.
    const parseResult = responseSchema.safeParse(data);
    if (parseResult.success) return returnValue;
    return {
      error: { status: 400, data: parseResult.error.format() },
    };
  }

  // Return the original returnValue object
  return returnValue;
};

/**
 * @description Base query used for all RTK Queries app-wide.
 * Will reauth with refresh token and validate against responseSchema if applicable.
 */
const baseQueryWithReauth: BaseQuery = async (args, api, extraOptions) => {
  // Run the provided query
  const state = api.getState() as RootState;
  let accessToken = '';
  let refreshToken = '';
  if (state.user.role !== 'public' && state.user.tokens) {
    accessToken = state.user.tokens.access.token;
    refreshToken = state.user.tokens.refresh.token;
  }
  let result = await baseQueryWithAuthAndZod(args, api, {
    ...extraOptions,
    token: accessToken,
  });

  // Retry if forbidden
  if (result.error && result.error.status === 403) {
    // Get new access token using refresh token
    const refreshResult = await baseQueryWithAuthAndZod(
      { url: '/auth/refresh', method: 'GET' },
      api,
      { responseSchema: AuthResponseSchema, token: refreshToken },
    );
    // Retry with new credentials or logout
    if (refreshResult.data) {
      const refreshData = refreshResult.data as AuthResponse;
      const newAccessToken = refreshData.tokens
        ? refreshData.tokens.access.token
        : '';
      api.dispatch(setCredentials(refreshData));
      api.dispatch(setTokens(refreshData));
      result = await baseQueryWithAuthAndZod(args, api, {
        ...extraOptions,
        // token: refreshData.tokens.access.token,
        token: newAccessToken,
      });
    } else {
      api.dispatch(logout());
      api.dispatch(removeTokens());
    }
  }

  return result;
};

export default baseQueryWithReauth;
